import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { X } from '@phosphor-icons/react';
import { Api, useApiCallable } from '../../api';
import { useState } from 'react';
import { format, parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { useAuthUser } from '../../hooks';
import { Model } from '../../models';

interface Props {
  open: boolean;
  onClose: () => void;
  appointment: Api.Appointment.V_ClinicianSub;
  clinicianId: string;
  onSuccess?: () => void;
}

export default function RescheduleAppointmentModal({
  open,
  onClose,
  appointment,
  clinicianId,
  onSuccess,
}: Props) {
  const callApi = useApiCallable();
  const user = useAuthUser();
  const timezone = user.timezone;
  const today = format(new Date(), 'yyyy-MM-dd');

  const initialDate = appointment.startAt
    ? formatInTimeZone(parseISO(appointment.startAt), timezone, 'yyyy-MM-dd')
    : today;

  const [date, setDate] = useState(initialDate);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const availabilityApi =
    Api.ClinicianAvailability.useReadCollectionAvailabilitiesStartDateEndDate(
      clinicianId,
      date,
      date,
    );

  const slots = availabilityApi?.data?.member || [];
  const options: Array<{ time: string; slot: string; duration: number }> = [];

  slots.forEach((r: Api.ClinicianAvailability.V) => {
    const slotDate = parseISO(r.slot);
    const timeDisplay = formatInTimeZone(slotDate, timezone, 'HH:mm');
    options.push({ time: timeDisplay, slot: r.slot, duration: r.duration });
  });

  const selectedOption = options.find((opt) => opt.slot === selectedSlot);
  const selectedTime = selectedSlot
    ? formatInTimeZone(parseISO(selectedSlot), timezone, 'HH:mm')
    : '';

  const endTimeText =
    selectedTime && selectedOption
      ? Model.Appointment.addMinutesToTime(
          selectedTime,
          selectedOption.duration,
        )
      : '';

  const handleDateChange = (newDate: string) => {
    setDate(newDate);
    setSelectedSlot(null);
  };

  const onSave = async () => {
    setSaving(true);
    await callApi(`/appointments/${appointment.id}/reschedule`, {
      method: 'PATCH',
      jsonBody: { startAt: selectedSlot! } as Api.Appointment.W_Reschedule,
    });
    setSaving(false);
    onSuccess?.();
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {appointment
          ? `Reschedule Appointment with ${appointment.user.name || appointment.user.id}`
          : 'Reschedule Appointment'}
        <IconButton onClick={onClose} size="small">
          <X size={20} />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2, bgcolor: '#DEE3E6' }} icon={false}>
          <Typography sx={{ fontWeight: 600 }}>
            Please reschedule an appointment only after confirming with the
            client
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You can reach out via chat or email to coordinate a suitable time
            for rescheduling the appointment.
          </Typography>
        </Alert>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            label="Date"
            type="date"
            fullWidth
            value={date}
            onChange={(e) => handleDateChange(e.target.value)}
            slotProps={{
              inputLabel: { shrink: true },
              htmlInput: { min: today },
            }}
          />
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              width: '100%',
            }}
          >
            <Box sx={{ flex: 1 }}>
              <FormControl fullWidth>
                <InputLabel id="reschedule-time-label">Time</InputLabel>
                <Select
                  labelId="reschedule-time-label"
                  label="Time"
                  fullWidth
                  value={selectedSlot}
                  displayEmpty
                  onChange={(e) => setSelectedSlot(e.target.value)}
                  renderValue={(v) => (v ? selectedTime : 'Select time')}
                >
                  {options.map((opt) => (
                    <MenuItem key={opt.slot} value={opt.slot}>
                      {opt.time}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Typography color="text.secondary" sx={{ alignSelf: 'end', mb: 1 }}>
              {selectedTime ? `ends ${endTimeText}` : ''}
            </Typography>
          </Box>
        </Stack>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 1, display: 'block' }}
        >
          You can only select time blocks you've marked as available in your
          schedule.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          onClick={onSave}
          disabled={saving || !selectedSlot}
        >
          Save
        </Button>
        <Button variant="outlined" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
